import React from 'react';
import { resolveTaskTx, taskIsInflow } from '../utils/paymentTask';

// ===========================================================================
// THE NOTICE CARD'S ACTIONS — one renderer for the pending-task pool
// (PendingTaskList: dashboard pop-up + Task Tracking "Unclaimed") and Task
// Tracking's own cards (My Tasks / All Tasks), so the two can never offer
// different buttons for the same notice (operator 2026-10-12).
//
//   nobody holds it      → Claim + the actions (acting claims it for you —
//                          server-side, in the same step)
//   you hold it          → "Claimed by you" + the actions + Release
//   someone else holds it→ "Claimed by X" only — the server refuses everyone
//                          else (409 NOTICE_CLAIMED), so nothing is offered
//   handled (Done)       → nothing to do; the stage bar says who handled it
//
// Which actions by role (unchanged): sales create the order / platform order
// (bank credits only) and assign to a customer; finance and administrators
// assign, mark as swap and classify. `actions` is the hooks/useTaskActions
// object of the hosting list; `onRelease` its own release handler (the ONE
// release endpoint either way).
// ===========================================================================
function NoticeTaskActions({ task, user, actions, onRelease, showClaim = true }) {
  if (!task || task.task_type !== 'pr_notification') return null;
  if (task.status === 'completed' || task.status === 'dismissed') return null;
  const role = user?.role;
  const held = task.claimed_by != null;
  const mine = held && Number(task.claimed_by) === Number(user?.id);
  if (held && !mine) {
    return (
      <div className="action-buttons">
        <span className="claimed-pill" title="Only the person holding a notice can act on it — they can release it back to the pool.">
          {`Claimed by ${task.claimed_by_username || 'another user'}`}
        </span>
      </div>
    );
  }
  const {
    claimingNoticeId, handleClaimNotice, handleCreateOrderFromNotice, handleCreatePlatformOrderFromNotice,
    handleAssignToCustomer, handleTxAction,
  } = actions || {};
  // "Create platform order" links a BANK credit to a P2P sell — on-chain /
  // wallet notices are not platform activity.
  const isBankPr = !!task.transaction_id && !task.blockchain_transaction_id && !task.crypto_transaction_id;
  const canClassify = (role === 'finance' || role === 'administrator') && !!resolveTaskTx(task);
  return (
    <div className="action-buttons">
      {mine ? (
        <span className="claimed-pill claimed-pill--self">Claimed by you</span>
      ) : showClaim && (
        <button
          onClick={() => handleClaimNotice(task)}
          className="btn btn-small btn-warning"
          disabled={claimingNoticeId === task.id}
          title="Take this notice — it leaves everybody else's pool and only you can act on it until you release it"
        >
          {claimingNoticeId === task.id ? 'Claiming…' : 'Claim'}
        </button>
      )}
      {role === 'sales' && (
        <button onClick={() => handleCreateOrderFromNotice(task)} className="btn btn-small btn-primary">
          Create order
        </button>
      )}
      {role === 'sales' && isBankPr && (
        <button onClick={() => handleCreatePlatformOrderFromNotice(task)} className="btn btn-small btn-outline">
          Create platform order
        </button>
      )}
      {/* Assign to customer — every role the server lets assign (the three
          assign endpoints constrain SALES only to customers they may act on). */}
      {(role === 'sales' || role === 'finance' || role === 'administrator') && (
        <button onClick={() => handleAssignToCustomer(task)} className="btn btn-small btn-outline">
          Assign to customer
        </button>
      )}
      {/* Finance/admin action the transaction directly (the same modal in
          Transaction History). Classify options are direction-aware. */}
      {canClassify && (
        <>
          <button onClick={() => handleTxAction(task, 'swap')} className="btn btn-small btn-info" title="Mark as internal swap (link to another wallet/account)">
            Mark swap
          </button>
          {taskIsInflow(task) ? (
            <>
              <button onClick={() => handleTxAction(task, 'profit')} className="btn btn-small btn-success" title="Classify this inflow as profit (P&L gain)">Profit</button>
              <button onClick={() => handleTxAction(task, 'deposit')} className="btn btn-small btn-outline" title="Record as deposit (capital in, non-P&L)">Deposit</button>
            </>
          ) : (
            <>
              <button onClick={() => handleTxAction(task, 'expense')} className="btn btn-small btn-secondary" title="Classify this outflow as expense (P&L cost)">Expense</button>
              <button onClick={() => handleTxAction(task, 'withdrawal')} className="btn btn-small btn-warning" title="Record as withdrawal (capital out, non-P&L)">Withdraw</button>
            </>
          )}
        </>
      )}
      {mine && onRelease && (
        <button onClick={() => onRelease(task)} className="btn btn-small btn-outline"
          title="Give this notice back — it returns to the sales and finance pools, unclaimed">
          Release
        </button>
      )}
      {!held && (
        <small style={{ color: '#6b7280', alignSelf: 'center' }}>
          Acting on it claims it for you.
        </small>
      )}
    </div>
  );
}

export default NoticeTaskActions;
